import { Component, Input, OnChanges, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { AzButtonComponent } from '../../../../../../shared/components/az-button/az-button.component';
import { StatusChangeModalComponent } from '../../../../../../shared/components/status-change-modal/status-change-modal.component';
import { UserPermissionsService } from '../../../../../../core/auth/services/user-permissions.service';
import { LoggerService } from '../../../../../../core/logging/logger.service';
import { SelectedLegalEntityService } from '../../../../../../shared/services/selected-legal-entity.service';
import { LegalEntityService } from '../../../../services/legal-entity.service';
import { IgaCoordinatorDto } from '../../../../models/legal-entity.models';
import { AzSelectorComponent } from '../../../../../../shared/components/az-selector/az-selector.component';

@Component({
  selector: 'app-iga-coordinator',
  imports: [AzButtonComponent, AzSelectorComponent, StatusChangeModalComponent],
  templateUrl: './iga-coordinator.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './iga-coordinator.component.scss',
})
export class IgaCoordinatorComponent implements OnChanges {
  @Input() legalEntityId: number | null = null;
  @Input() selectedCoordinatorId: number | null = null;
  @Input() selectedCoordinatorName = '';

  coordinators: IgaCoordinatorDto[] = [];
  currentCoordinatorId: number | null = null;
  currentCoordinatorName = '';
  isEditing = false;
  isLoadingOptions = false;
  isSaving = false;
  showSigningConfirmation = false;
  showSigningError = false;
  signingErrorTitle = '';
  signingErrorMessage = '';

  private originalCoordinatorId: number | null = null;
  private hasLoadedOptions = false;

  constructor(
    private legalEntityService: LegalEntityService,
    public userPermissions: UserPermissionsService,
    private logger: LoggerService,
    private selectedLegalEntity: SelectedLegalEntityService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['legalEntityId'] && !changes['legalEntityId'].firstChange) {
      this.isEditing = false;
      this.isSaving = false;
      this.coordinators = [];
      this.hasLoadedOptions = false;
      this.syncSelectionFromInputs();
      return;
    }

    if (
      !this.isEditing &&
      (changes['selectedCoordinatorId'] || changes['selectedCoordinatorName'])
    ) {
      this.syncSelectionFromInputs();
    }
  }

  get canEdit(): boolean {
    return this.userPermissions.hasPstMemberRole() || this.userPermissions.hasPrivacyOfficeRole();
  }

  get hasChanges(): boolean {
    return this.currentCoordinatorId !== this.originalCoordinatorId;
  }

  get canClearCoordinatorSelection(): boolean {
    return this.originalCoordinatorId === null;
  }

  get coordinatorSelectorValue(): string {
    return (
      this.coordinatorOptionEntries.find((option) => option.id === this.currentCoordinatorId)
        ?.label ?? ''
    );
  }

  get coordinatorOptions(): string[] {
    return this.coordinatorOptionEntries.map((option) => option.label);
  }

  toggleEdit(): void {
    if (!this.canEdit || this.isSaving) {
      return;
    }

    if (this.isEditing) {
      this.discard();
      return;
    }

    this.originalCoordinatorId = this.currentCoordinatorId;
    this.isEditing = true;
    this.loadCoordinatorOptions();
  }

  onCoordinatorChange(value: string): void {
    const selectedOption = this.coordinatorOptionEntries.find((option) => option.label === value);
    if (!selectedOption) {
      return;
    }

    this.currentCoordinatorId = selectedOption.id;
    this.currentCoordinatorName = selectedOption.displayName;
  }

  discard(): void {
    this.currentCoordinatorId = this.originalCoordinatorId;
    this.currentCoordinatorName =
      this.coordinators.find((coordinator) => coordinator.id === this.originalCoordinatorId)
        ?.display_name ?? this.selectedCoordinatorName;
    this.isEditing = false;
  }

  saveChanges(): void {
    if (this.legalEntityId === null || !this.canEdit || !this.hasChanges || this.isSaving) {
      return;
    }

    if (this.isActiveSigningStatus) {
      this.showSigningConfirmation = true;
      return;
    }

    this.updateCoordinator(false);
  }

  get isActiveSigningStatus(): boolean {
    return (
      this.selectedLegalEntity.entityDetail()?.bcr_status?.status_name === 'Pending: Pending LoA'
    );
  }

  cancelSigningConfirmation(): void {
    this.showSigningConfirmation = false;
  }

  confirmSigningChange(): void {
    this.showSigningConfirmation = false;
    this.updateCoordinator(true);
  }

  dismissSigningError(): void {
    this.showSigningError = false;
  }

  private updateCoordinator(isActiveSigningUpdate: boolean): void {
    if (this.legalEntityId === null) {
      return;
    }

    this.isSaving = true;
    this.legalEntityService
      .updateIgaCoordinator(this.legalEntityId, {
        iga_coordinator_id: this.currentCoordinatorId,
      })
      .subscribe({
        next: () => {
          this.originalCoordinatorId = this.currentCoordinatorId;
          this.currentCoordinatorName =
            this.coordinators.find((coordinator) => coordinator.id === this.currentCoordinatorId)
              ?.display_name ?? '';
          this.updateSharedCoordinatorDetail();
          this.isEditing = false;
          this.isSaving = false;
        },
        error: (error) => {
          this.logger.error('Error updating IGA Coordinator:', error);
          const message = error?.error?.message;
          if (isActiveSigningUpdate) {
            const rollbackFailed =
              typeof message === 'string' && message.includes('IGA Coordinator rollback failed');

            if (rollbackFailed) {
              this.originalCoordinatorId = this.currentCoordinatorId;
              this.updateSharedCoordinatorDetail();
              this.signingErrorTitle = 'IGA Coordinator Rollback Failed';
              this.signingErrorMessage =
                'The signing process could not be restarted and the previous IGA Coordinator could not be restored. Please contact support.';
            } else {
              this.currentCoordinatorId = this.originalCoordinatorId;
              this.currentCoordinatorName =
                this.coordinators.find(
                  (coordinator) => coordinator.id === this.originalCoordinatorId,
                )?.display_name ?? this.selectedCoordinatorName;
              this.signingErrorTitle = 'Signing Restart Failed';
              this.signingErrorMessage =
                'The signing process could not be restarted, and the previous IGA Coordinator was restored.';
            }

            this.isEditing = false;
            this.showSigningError = true;
          }
          this.isSaving = false;
        },
      });
  }

  private updateSharedCoordinatorDetail(): void {
    const detail = this.selectedLegalEntity.entityDetail();
    if (!detail) {
      return;
    }

    const coordinatorName =
      this.currentCoordinatorId === null
        ? null
        : (this.coordinators.find((coordinator) => coordinator.id === this.currentCoordinatorId)
            ?.display_name ?? this.currentCoordinatorName);
    this.selectedLegalEntity.setEntityDetail({
      ...detail,
      legal_entity: {
        ...detail.legal_entity,
        iga_coordinator_id: this.currentCoordinatorId,
        iga_coordinator_name: coordinatorName,
      },
    });
  }

  private loadCoordinatorOptions(): void {
    if (!this.canEdit || this.hasLoadedOptions || this.isLoadingOptions) {
      return;
    }

    this.isLoadingOptions = true;
    this.legalEntityService.getIgaCoordinators().subscribe({
      next: (coordinators) => {
        this.coordinators = coordinators;
        this.hasLoadedOptions = true;
        this.isLoadingOptions = false;
      },
      error: (error) => {
        this.logger.error('Error loading IGA Coordinators:', error);
        this.isLoadingOptions = false;
      },
    });
  }

  private syncSelectionFromInputs(): void {
    this.currentCoordinatorId = this.selectedCoordinatorId;
    this.currentCoordinatorName = this.selectedCoordinatorName || '';
    this.originalCoordinatorId = this.selectedCoordinatorId;
  }

  private get coordinatorOptionEntries(): Array<{
    id: number;
    displayName: string;
    label: string;
  }> {
    const entries = this.coordinators.map((coordinator) => ({
      id: coordinator.id,
      displayName: coordinator.display_name,
    }));

    if (
      this.currentCoordinatorId !== null &&
      !entries.some((entry) => entry.id === this.currentCoordinatorId)
    ) {
      entries.unshift({
        id: this.currentCoordinatorId,
        displayName:
          this.currentCoordinatorName ||
          this.selectedCoordinatorName ||
          String(this.currentCoordinatorId),
      });
    }

    const nameCounts = new Map<string, number>();
    entries.forEach(({ displayName }) => {
      nameCounts.set(displayName, (nameCounts.get(displayName) ?? 0) + 1);
    });

    return entries.map((entry) => ({
      ...entry,
      label:
        nameCounts.get(entry.displayName)! > 1
          ? `${entry.displayName} (${entry.id})`
          : entry.displayName,
    }));
  }
}
